import { useEffect, useMemo, useState } from "react";
import { Modal } from "react-bootstrap";
import { CgEye, CgPen, CgTrash } from "react-icons/cg";
import { AuthInterceptor } from "../../services/AuthInterceptor.Service";
import CustomModal from "./CustomModal";
import Spinnerr from "./Spinner";
import Toaster from "./Toaster";
import SearchableMultiSelectModal from "./SearchableMultiSelectModal";

const readValue = (row, key) =>
  key.split(".").reduce((value, part) => value?.[part], row);

const displayValue = (key, value) => {
  if (key === "userDetails" && value && typeof value === "object") {
    return (
      <div className="d-grid gap-1">
        <div>
          <span className="text-muted me-2">Name:</span>
          <span className="fw-semibold">{value.fullName || "-"}</span>
        </div>
        <div>
          <span className="text-muted me-2">Email:</span>
          <span className="fw-semibold">{value.email || "-"}</span>
        </div>
        <div>
          <span className="text-muted me-2">Mobile:</span>
          <span className="fw-semibold">{value.mobileNumber || "-"}</span>
        </div>
        <div>
          <span className="text-muted me-2">Status:</span>
          <span className="fw-semibold">{value.isActive ? "Active" : "Inactive"}</span>
        </div>
      </div>
    );
  }

  if (Array.isArray(value)) {
    return value.length ? value.join(", ") : "-";
  }

  if (value && typeof value === "object") {
    return JSON.stringify(
      value,
      (objectKey, item) =>
        objectKey.toLowerCase().includes("_id") || objectKey === "__v" ? undefined : item
    );
  }
  return String(value ?? "-");
};

const getListFromResponse = (data) => {
  if (Array.isArray(data?.r)) return data.r;
  if (Array.isArray(data?.r?.data)) return data.r.data;
  if (Array.isArray(data?.data)) return data.data;
  if (Array.isArray(data?.result)) return data.result;
  return [];
};

const buildBody = (fields, form, mode) => {
  const body = {};
  fields.forEach((field) => {
    if (mode === "edit" && field.hideOnEdit) return;
    if (mode === "add" && field.hideOnAdd) return;

    const rawValue = form[field.name];
    if (field.type === "array") {
      body[field.name] = String(rawValue || "")
        .split(",")
        .map((item) => item.trim())
        .filter(Boolean);
      return;
    }

    if (field.type === "multiselect" || field.type === "modal-multiselect") {
      body[field.name] = Array.isArray(rawValue) ? rawValue : [];
      return;
    }

    if (field.type === "number") {
      body[field.name] = rawValue === "" ? "" : Number(rawValue);
      return;
    }

    if (field.type === "checkbox") {
      body[field.name] = Boolean(rawValue);
      return;
    }

    body[field.name] = rawValue;
  });
  return body;
};

const AdminCrudPage = ({
  title,
  listPath,
  addPath,
  editPath,
  deletePath,
  bulkDeletePath,
  getByIdPath,
  statusPath,
  addButtonLabel,
  fields = [],
  columns = [],
  searchFields = [],
  transformBody,
  statusBody,
  statusMethod = "PATCH",
  statusLabel = "isActive",
  showStatusAction = true,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState(null);
  const [query, setQuery] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(10);
  const [modalMode, setModalMode] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({});
  const [deleteId, setDeleteId] = useState(null);
  const [deleteIds, setDeleteIds] = useState([]);
  const [selectedIds, setSelectedIds] = useState([]);

  const listUrl = `${listPath}${listPath.includes("?") ? "&" : "?"}page=${pageNo}&limit=${limit}`;

  const showSnack = (message, type = "success") => {
    setSnack({ notification: { message, type } });
  };

  const loadRows = async () => {
    setLoading(true);
    try {
      const response = await AuthInterceptor(listUrl, { method: "GET" });
      if (response?.data?.s === 200 || response?.status === 200) {
        setRows(getListFromResponse(response.data));
      } else {
        setRows([]);
      }
    } catch (error) {
      setRows([]);
      showSnack(`Error fetching ${title}`, "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageNo, limit, listPath]);

  const filteredRows = useMemo(() => {
    if (!query.trim()) return rows;
    const search = query.toLowerCase();
    return rows.filter((row) =>
      searchFields.some((field) =>
        String(readValue(row, field) || "")
          .toLowerCase()
          .includes(search)
      )
    );
  }, [query, rows, searchFields]);

  const paginatedRows = filteredRows.slice(0, limit);
  const totalPages = Math.max(1, Math.ceil(filteredRows.length / limit));
  const visibleIds = paginatedRows.map((row) => row._id).filter(Boolean);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));

  const openAdd = () => {
    const initialForm = {};
    fields.forEach((field) => {
      initialForm[field.name] =
        field.defaultValue ??
        (field.type === "checkbox"
          ? false
          : field.type === "multiselect" || field.type === "modal-multiselect"
          ? []
          : "");
    });
    setForm(initialForm);
    setSelected(null);
    setModalMode("add");
  };

  const openEdit = (row) => {
    const initialForm = {};
    fields.forEach((field) => {
      const value = readValue(row, field.name);
      initialForm[field.name] = Array.isArray(value) ? value.join(",") : value ?? "";
      if (field.type === "multiselect" || field.type === "modal-multiselect") {
        initialForm[field.name] = Array.isArray(value)
          ? value.map((item) => (typeof item === "object" ? item.path || item.value || item._id : item)).filter(Boolean)
          : [];
      }
    });
    setSelected(row);
    setForm(initialForm);
    setModalMode("edit");
  };

  const openView = async (row) => {
    if (!getByIdPath) {
      setSelected(row);
      setModalMode("view");
      return;
    }

    setLoading(true);
    try {
      const response = await AuthInterceptor(getByIdPath(row._id), {
        method: "GET",
      });
      setSelected(response?.data?.r || response?.data?.data || row);
      setModalMode("view");
    } catch (error) {
      showSnack(`Error loading ${title} details`, "error");
    } finally {
      setLoading(false);
    }
  };

  const saveRecord = async (event) => {
    event.preventDefault();
    const mode = modalMode;
    const url = mode === "edit" ? editPath?.(selected?._id) : addPath;
    if (!url) return;

    const rawBody = buildBody(fields, form, mode);
    const body = transformBody ? transformBody(rawBody, mode, selected) : rawBody;

    setLoading(true);
    try {
      const response = await AuthInterceptor(url, {
        method: mode === "edit" ? "PUT" : "POST",
        body: JSON.stringify(body),
      });
      const ok = [200, 201].includes(response?.data?.s) || [200, 201].includes(response?.status);
      if (ok) {
        showSnack(`${title} ${mode === "edit" ? "updated" : "added"} successfully`);
        setModalMode(null);
        loadRows();
      } else {
        showSnack(response?.data?.m || `Error saving ${title}`, "error");
      }
    } catch (error) {
      showSnack(`Error saving ${title}`, "error");
    } finally {
      setLoading(false);
    }
  };

  const deleteRecord = async () => {
    const ids = deleteIds.length ? deleteIds : deleteId ? [deleteId] : [];
    if (!ids.length || (!deletePath && !bulkDeletePath)) return;
    setLoading(true);
    try {
      const response = await AuthInterceptor(bulkDeletePath || deletePath(ids[0]), {
        method: "DELETE",
        body: bulkDeletePath ? JSON.stringify({ ids }) : undefined,
      });
      if (response?.data?.s === 200 || response?.status === 200) {
        showSnack(response?.data?.m || `${title} deleted successfully`);
        setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
        loadRows();
      } else {
        showSnack(response?.data?.m || `Error deleting ${title}`, "error");
      }
    } catch (error) {
      showSnack(`Error deleting ${title}`, "error");
    } finally {
      setLoading(false);
      setDeleteId(null);
      setDeleteIds([]);
    }
  };

  const toggleRowSelection = (id) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((selectedId) => selectedId !== id) : [...prev, id]
    );
  };

  const toggleVisibleSelection = () => {
    setSelectedIds((prev) =>
      allVisibleSelected
        ? prev.filter((id) => !visibleIds.includes(id))
        : Array.from(new Set([...prev, ...visibleIds]))
    );
  };

  const runBulkAction = async (action) => {
    if (!selectedIds.length) {
      showSnack(`Select at least one ${title.toLowerCase()}`, "error");
      return;
    }

    if (action === "delete" && !deletePath && !bulkDeletePath) return;
    if (action !== "delete" && !statusPath) return;

    if (action === "delete") {
      setDeleteId(null);
      setDeleteIds(selectedIds);
      return;
    }

    setLoading(true);
    try {
      await Promise.all(
        selectedIds.map((id) => {
          const row = rows.find((item) => item._id === id);
          const nextStatus = action === "activate";
          if (Boolean(row?.[statusLabel]) === nextStatus) return Promise.resolve();
          return AuthInterceptor(statusPath(id), {
            method: statusMethod,
            body: JSON.stringify(statusBody ? statusBody(row, nextStatus) : { isActive: nextStatus }),
          });
        })
      );
      showSnack("Bulk action completed");
      setSelectedIds([]);
      loadRows();
    } catch (error) {
      showSnack("Bulk action failed", "error");
    } finally {
      setLoading(false);
    }
  };

  const toggleStatus = async (row) => {
    if (!statusPath) return;
    const nextStatus = !Boolean(row[statusLabel]);
    setLoading(true);
    try {
      const response = await AuthInterceptor(statusPath(row._id), {
        method: statusMethod,
        body: JSON.stringify(statusBody ? statusBody(row, nextStatus) : { isActive: nextStatus }),
      });
      if (response?.data?.s === 200 || response?.status === 200) {
        showSnack(`${title} status updated`);
        loadRows();
      } else {
        showSnack(response?.data?.m || `Error updating ${title} status`, "error");
      }
    } catch (error) {
      showSnack(`Error updating ${title} status`, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading && <Spinnerr show={loading} />}
      {snack && <Toaster notification={snack.notification} onClose={() => setSnack(null)} />}

      <div className="card shadow-sm border-0 rounded-3 p-3">
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-3 gap-2">
          <h2 className="mb-0">{title}</h2>
          <div className="d-flex gap-2">
            <select
              className="form-select form-select-sm w-auto"
              value={limit}
              onChange={(e) => {
                setPageNo(1);
                setLimit(Number(e.target.value));
              }}
            >
              {[5, 10, 20, 50].map((size) => (
                <option key={size} value={size}>
                  {size} per page
                </option>
              ))}
            </select>
            {addPath && (
              <button className="btn btn-outline-custom btn-sm" onClick={openAdd}>
                {addButtonLabel || `Add ${title}`}
              </button>
            )}
          </div>
        </div>

        <div className="input-group mb-3">
          <input
            className="form-control"
            placeholder={`Search ${title.toLowerCase()}...`}
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setPageNo(1);
            }}
          />
          <button className="btn btn-outline-secondary">Search</button>
        </div>

        {(deletePath || bulkDeletePath || statusPath) && (
          <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
            <span className="text-muted small">{selectedIds.length} selected</span>
            {statusPath && (
              <>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => runBulkAction("activate")}>
                  Active
                </button>
                <button className="btn btn-sm btn-outline-secondary" onClick={() => runBulkAction("inactive")}>
                  Inactive
                </button>
              </>
            )}
        {(deletePath || bulkDeletePath) && (
              <button className="btn btn-sm btn-outline-danger" onClick={() => runBulkAction("delete")}>
                Delete
              </button>
            )}
          </div>
        )}

        <div className="table-responsive">
          <table className="table table-hover align-middle" style={{ tableLayout: "fixed", width: "99%" }}>
            <thead className="table-light">
              <tr>
                {(deletePath || bulkDeletePath || statusPath) && (
                  <th style={{ width: "48px" }}>
                    <input
                      type="checkbox"
                      className="form-check-input"
                      checked={allVisibleSelected}
                      onChange={toggleVisibleSelection}
                      aria-label={`Select all ${title.toLowerCase()} on this page`}
                    />
                  </th>
                )}
                <th>Sr.</th>
                {columns.map((column) => (
                  <th key={column.key}>{column.label}</th>
                ))}
                <th style={{ width: "180px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paginatedRows.length > 0 ? (
                paginatedRows.map((row, index) => (
                  <tr key={row._id || index}>
                    {(deletePath || bulkDeletePath || statusPath) && (
                      <td>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          checked={selectedIds.includes(row._id)}
                          onChange={() => toggleRowSelection(row._id)}
                          aria-label={`Select ${title.toLowerCase()} row`}
                        />
                      </td>
                    )}
                    <td>{(pageNo - 1) * limit + index + 1}</td>
                    {columns.map((column) => {
                      const value = readValue(row, column.key);
                      return (
                        <td key={column.key}>
                          {column.render
                            ? column.render(value, row, { toggleStatus })
                            : Array.isArray(value)
                            ? value.length
                            : value || "-"}
                        </td>
                      );
                    })}
                    <td>
                      <div className="d-flex gap-2">
                        <button className="btn btn-sm btn-outline-info" onClick={() => openView(row)}>
                          <CgEye size={16} />
                        </button>
                        {editPath && (
                          <button className="btn btn-sm btn-outline-custom" onClick={() => openEdit(row)}>
                            <CgPen size={16} />
                          </button>
                        )}
                        {statusPath && showStatusAction && (
                          <button className="btn btn-sm btn-outline-secondary" onClick={() => toggleStatus(row)}>
                            {row[statusLabel] ? "Active" : "Inactive"}
                          </button>
                        )}
                        {(deletePath || bulkDeletePath) && (
                          <button className="btn btn-sm btn-outline-danger" onClick={() => {
                            setDeleteId(row._id);
                            setDeleteIds([]);
                          }}>
                            <CgTrash size={16} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={columns.length + 2 + (deletePath || bulkDeletePath || statusPath ? 1 : 0)} className="text-center text-muted py-4">
                    No {title.toLowerCase()} found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="d-flex justify-content-between align-items-center mt-3">
          <button
            className="btn btn-outline-custom btn-sm"
            disabled={pageNo === 1}
            onClick={() => setPageNo((page) => Math.max(1, page - 1))}
          >
            Prev
          </button>
          <span>
            Page <strong>{pageNo}</strong> of <strong>{totalPages}</strong>
          </span>
          <button
            className="btn btn-outline-custom btn-sm"
            disabled={pageNo >= totalPages && rows.length < limit}
            onClick={() => setPageNo((page) => page + 1)}
          >
            Next
          </button>
        </div>
      </div>

      <Modal show={modalMode === "add" || modalMode === "edit"} onHide={() => setModalMode(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>{modalMode === "edit" ? `Edit ${title}` : addButtonLabel || `Add ${title}`}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <form onSubmit={saveRecord}>
            {fields
              .filter((field) => !(modalMode === "edit" && field.hideOnEdit) && !(modalMode === "add" && field.hideOnAdd))
              .map((field) => (
                <div className="mb-3" key={field.name}>
                  <label className="form-label">{field.label}</label>
                  {field.type === "select" ? (
                    <select
                      className="form-select"
                      value={form[field.name] || ""}
                      onChange={(e) => setForm((prev) => ({ ...prev, [field.name]: e.target.value }))}
                    >
                      <option value="">Select {field.label}</option>
                      {(field.options || []).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : field.type === "modal-multiselect" ? (
                    <SearchableMultiSelectModal
                      title={`Select ${field.label}`}
                      options={field.options || []}
                      selectedValues={form[field.name] || []}
                      onChange={(values) => setForm((prev) => ({ ...prev, [field.name]: values }))}
                      buttonLabel={`Select ${field.label}`}
                      emptyLabel={`No ${field.label.toLowerCase()} selected`}
                    />
                  ) : field.type === "multiselect" ? (
                    <select
                      className="form-select"
                      multiple
                      value={form[field.name] || []}
                      onChange={(e) =>
                        setForm((prev) => ({
                          ...prev,
                          [field.name]: Array.from(e.target.selectedOptions).map((option) => option.value),
                        }))
                      }
                    >
                      {(field.options || []).map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  ) : field.type === "textarea" || field.type === "array" ? (
                    <textarea
                      className="form-control"
                      rows={field.type === "array" ? 2 : 3}
                      value={form[field.name] || ""}
                      placeholder={field.placeholder}
                      onChange={(e) => setForm((prev) => ({ ...prev, [field.name]: e.target.value }))}
                    />
                  ) : field.type === "checkbox" ? (
                    <div className="form-check form-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={Boolean(form[field.name])}
                        onChange={(e) => setForm((prev) => ({ ...prev, [field.name]: e.target.checked }))}
                      />
                    </div>
                  ) : (
                    <input
                      className="form-control"
                      type={field.type || "text"}
                      value={form[field.name] || ""}
                      placeholder={field.placeholder}
                      onChange={(e) => setForm((prev) => ({ ...prev, [field.name]: e.target.value }))}
                    />
                  )}
                </div>
              ))}
            <div className="d-flex justify-content-end gap-2">
              <button type="button" className="btn btn-outline-secondary" onClick={() => setModalMode(null)}>
                Cancel
              </button>
              <button type="submit" className="btn btn-outline-custom">
                Save
              </button>
            </div>
          </form>
        </Modal.Body>
      </Modal>

      <Modal show={modalMode === "view"} onHide={() => setModalMode(null)} centered size="lg">
        <Modal.Header closeButton>
          <Modal.Title>{title} Details</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="table-responsive">
            <table className="table table-sm">
              <tbody>
                {selected &&
                  Object.entries(selected)
                    .filter(([key]) => !key.toLowerCase().includes("_id") && key !== "__v")
                    .map(([key, value]) => (
                    <tr key={key}>
                      <th style={{ width: "220px" }}>{key}</th>
                      <td>{displayValue(key, value)}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </Modal.Body>
      </Modal>

      <CustomModal
        show={Boolean(deleteId) || deleteIds.length > 0}
        title="Confirm Deletion"
        message={`This action can not be reversed. Are you sure you want to delete ${deleteIds.length || 1} ${title.toLowerCase()}${(deleteIds.length || 1) > 1 ? " records" : " record"}?`}
        onConfirm={deleteRecord}
        onCancel={() => {
          setDeleteId(null);
          setDeleteIds([]);
        }}
      />
    </>
  );
};

export default AdminCrudPage;
